import { createFileRoute, Outlet } from '@tanstack/react-router'
import { useEffect, useState, createContext, useContext } from 'react'
import { Helmet } from '@/common/components/Helmet'
import PageNotFound from '@/common/components/views/PageNotFound'
import HgCircularProgressGlobal from '@/common/components/ui/atoms/HgCircularProgressGlobal'
import { requestOrganizationSurveyByUuid } from '@/features/surveys/api/requests'
import errorHandler from '@/common/lib/errorHandler'
import { OrganizationSurvey as OrganizationSurveyType } from '@/features/surveys/classes/SurveyModel'
import { validate as validateUuid } from 'uuid'
import { get } from 'lodash'
import { SurveyDemographicOption } from '@/features/surveys/classes/SurveyDemographicOptionModel'
import { requestCreateUserActivity } from '@/common/api/requests-activity-log'

type SurveyContextType = {
  survey: OrganizationSurveyType
  demographicOptions: SurveyDemographicOption[]
}

const SurveyContext = createContext<SurveyContextType | null>(null)

export const useSurvey = () => {
  const context = useContext(SurveyContext)
  if (!context) {
    throw new Error('useSurvey must be used within SurveyProvider')
  }
  return context
}

export const Route = createFileRoute('/app/surveys/$surveyOrganizationUuid')({
  component: RouteComponent,
})

function RouteComponent() {
  const { surveyOrganizationUuid } = Route.useParams()

  const [demographicOptions, setDemographicOptions] = useState<
    SurveyDemographicOption[]
  >([])
  const [survey, setSurvey] = useState<OrganizationSurveyType | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [notFound, setNotFound] = useState<boolean>(false)
  const [validatedUuid, setValidatedUuid] = useState<string | null>(null)

  // Validate UUID
  useEffect(() => {
    let newValidatedUuid = validateUuid(surveyOrganizationUuid)
      ? surveyOrganizationUuid
      : null
    setValidatedUuid(newValidatedUuid)
    setLoading(false)
    setNotFound(true)
  }, [surveyOrganizationUuid])

  // Load, populate survey
  useEffect(() => {
    if (validatedUuid) {
      setLoading(true)
      setNotFound(false)
      if (!validateUuid(validatedUuid)) {
        setLoading(false)
        setNotFound(true)
      } else {
        requestOrganizationSurveyByUuid(validatedUuid)
          .then((res) => {
            let s: OrganizationSurveyType | null = res?.data?.data || null
            let d: SurveyDemographicOption[] | null =
              res?.data?.meta.survey_demographic_options || null
            setDemographicOptions(d ? d : [])
            if (s) {
              setSurvey(s)
              // Log activity
              requestCreateUserActivity(
                'organization_survey',
                validatedUuid,
                'action',
              )
            } else {
              setSurvey(null)
              setNotFound(true)
            }
          })
          .catch((err) => {
            setNotFound(true)
            errorHandler(err, false, false)
          })
          .finally(() => {
            setLoading(false)
          })
      }
    }
  }, [validatedUuid])

  if (loading) {
    return <HgCircularProgressGlobal />
  }

  if (
    notFound ||
    !survey ||
    !get(survey, 'open', null) ||
    !get(survey, 'pivot.active', null)
  ) {
    return <PageNotFound />
  }

  if (!validatedUuid) {
    return null
  }

  return (
    <SurveyContext.Provider value={{ survey, demographicOptions }}>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Outlet />
    </SurveyContext.Provider>
  )
}
